import { createId } from "@paralleldrive/cuid2";
import {
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { z } from "zod";
import { createdAt, deletedAt, updatedAt } from "../lib/types";
import { cuid, dateTime, string } from "../validator/common";
import { users } from "./users";

// Training Programs table
export const trainingPrograms = sqliteTable(
  "training_programs",
  {
    programId: text("program_id")
      .primaryKey()
      .notNull()
      .$defaultFn(() => createId()),
    name: text("name").notNull(),
    description: text("description"),
    durationWeeks: integer("duration_weeks").notNull().default(1),
    daysPerWeek: integer("days_per_week").notNull().default(1),
    maxDaysPerWeek: integer("max_days_per_week"),
    difficulty: integer("difficulty").notNull().default(1), // 1-5 scale
    isActive: integer("is_active", { mode: "boolean" }).default(true).notNull(),
    isPublic: integer("is_public", { mode: "boolean" })
      .default(false)
      .notNull(),
    isPaid: integer("is_paid", { mode: "boolean" }).default(false).notNull(),
    price: integer("price"),
    oldPrice: integer("old_price"),
    // Kept only for compatibility with the existing database schema.
    shopierLink: text("shopier_link"),
    createdById: text("created_by_id").references(() => users.id),
    image: text("image"),
    accessCode: text("access_code").unique(),
    maxParticipants: integer("max_participants"),
    displayOrder: integer("display_order").notNull().default(0),
    introVideoUrl: text("intro_video_url"),
    createdAt,
    updatedAt,
    deletedAt,
  },
  (t) => [uniqueIndex("unique_program_id").on(t.programId)],
);

// Zod schemas
export const selectTrainingProgramSchema = z.object({
  programId: cuid,
  name: string.min(3, "Program adı en az 3 karakter olmalı"),
  description: string.nullable(),
  durationWeeks: z.number().int().min(1).max(52),
  daysPerWeek: z.number().int().min(1).max(7),
  maxDaysPerWeek: z.number().int().min(1).max(7).nullable().optional(),
  difficulty: z.number().int().min(1).max(5),
  isActive: z.boolean(),
  isPublic: z.boolean(),
  isPaid: z.boolean(),
  price: z.number().int().nullable().optional(),
  oldPrice: z.number().int().nullable().optional(),
  createdById: cuid.nullable(),
  image: string.nullable(),
  accessCode: string.nullable(),
  maxParticipants: z.number().int().nullable(),
  displayOrder: z.number().int(),
  introVideoUrl: string.nullable(),
  createdAt: dateTime,
  updatedAt: dateTime.nullable(),
  deletedAt: dateTime.nullable(),
});

export const insertTrainingProgramSchema = z.object({
  name: string.min(3, "Program adı en az 3 karakter olmalı"),
  description: string.optional(),
  durationWeeks: z.number().int().min(1).max(52).optional(),
  daysPerWeek: z.number().int().min(1).max(7).optional(),
  maxDaysPerWeek: z.number().int().min(1).max(7).nullable().optional(),
  difficulty: z.number().int().min(1).max(5).optional(),
  isActive: z.boolean().optional(),
  isPublic: z.boolean().optional(),
  isPaid: z.boolean().optional(),
  price: z.number().int().nullable().optional(),
  oldPrice: z.number().int().nullable().optional(),
  image: string.nullable().optional(),
  accessCode: string.nullable().optional(),
  maxParticipants: z.number().int().nullable().optional(),
  displayOrder: z.number().int().min(0).optional(),
  introVideoUrl: string.nullable().optional(),
});

export const patchTrainingProgramSchema = z.object({
  programId: cuid,
  name: string.min(3).optional(),
  description: string.nullable().optional(),
  durationWeeks: z.number().int().min(1).max(52).optional(),
  daysPerWeek: z.number().int().min(1).max(7).optional(),
  maxDaysPerWeek: z.number().int().min(1).max(7).nullable().optional(),
  difficulty: z.number().int().min(1).max(5).optional(),
  isActive: z.boolean().optional(),
  isPublic: z.boolean().optional(),
  isPaid: z.boolean().optional(),
  price: z.number().int().nullable().optional(),
  oldPrice: z.number().int().nullable().optional(),
  image: string.nullable().optional(),
  accessCode: string.nullable().optional(),
  maxParticipants: z.number().int().nullable().optional(),
  displayOrder: z.number().int().min(0).optional(),
  introVideoUrl: string.nullable().optional(),
});

export type SelectTrainingProgram = z.infer<typeof selectTrainingProgramSchema>;
export type InsertTrainingProgram = z.infer<typeof insertTrainingProgramSchema>;
export type PatchTrainingProgram = z.infer<typeof patchTrainingProgramSchema>;

/**
 * Formats days per week as a range (e.g. "3-4 Gün / Hafta") or single ("4 Gün / Hafta")
 */
export function formatDaysPerWeek(
  daysPerWeek?: number | null,
  maxDaysPerWeek?: number | null,
): string {
  if (!daysPerWeek) return "";
  if (maxDaysPerWeek && maxDaysPerWeek > daysPerWeek) {
    return `${daysPerWeek}-${maxDaysPerWeek} Gün / Hafta`;
  }
  return `${daysPerWeek} Gün / Hafta`;
}

/**
 * Formats days per week short format (e.g. "3-4 Gün" or "4 Gün")
 */
export function formatDaysPerWeekShort(
  daysPerWeek?: number | null,
  maxDaysPerWeek?: number | null,
): string {
  if (!daysPerWeek) return "";
  if (maxDaysPerWeek && maxDaysPerWeek > daysPerWeek) {
    return `${daysPerWeek}-${maxDaysPerWeek} Gün`;
  }
  return `${daysPerWeek} Gün`;
}
